import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute, Params } from '@angular/router';
import { PermissionPipe } from '@app/features/admin/settings/permission/pipes/permission.pipe';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { ContainerComponent } from '@shared/components/container/container.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { SelectComponent } from '@shared/components/form/select/select.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { RowComponent } from '@shared/components/row/row.component';
import { ToggleComponent } from '@shared/components/toggle/toggle.component';
import { Months } from '@shared/shared.constants';
import { RobActions } from '@vessel-reports/rob/state/rob.action';
import { RobState } from '@vessel-reports/rob/state/rob.state';
import { RobReportDetail } from '@vessel-reports/rob/state/rob.types';
import { VesselReportsAction } from '@vessel-reports/state/vessel-reports.action';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { find } from 'lodash-es';
import { Observable, Subject, take, takeUntil } from 'rxjs';

@Component({
  selector: 'create-rob-report',
  imports: [
    TranslocoPipe,
    BoxComponent,
    HeaderComponent,
    BodyComponent,
    FooterComponent,
    ContainerComponent,
    MatIcon,
    TextboxComponent,
    RowComponent,
    SelectComponent,
    ToggleComponent,
    PermissionPipe
  ],
  templateUrl: './create-rob-report.component.html',
  styleUrl: './create-rob-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateRobReportComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);

  @Output() save: EventEmitter<void> = new EventEmitter<void>(); // declare output
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  previousReport: Signal<RobReportDetail> = computed(() => this._state.previousReport());
  months: typeof Months = Months;
  form: FormGroup;
  queryParams: WritableSignal<Params> = signal(void 0);
  private readonly _vesselReportsActions: VesselReportsAction = inject(VesselReportsAction);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly _profileState: ProfileState = inject(ProfileState);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _action: RobActions = inject(RobActions);
  private readonly _state: RobState = inject(RobState);
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  currentStatusIndex: string;

  get lastReportTitle() {
    if (isEmpty(this.previousReport())) {
      return 'No Report Submitted';
    }
    const previousMonth = find(this.months, (month) => month.value === this.previousReport().month);
    return `${String(previousMonth?.name || '').toUpperCase()} ${this.previousReport().year}`;
  }

  ngOnInit() {
    this._subscribeToSource$(this._activatedRoute.paramMap, (paramMap) => {
      this.currentStatusIndex = paramMap.get('status');
    });

    this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
      this.queryParams.set({ ...queryParams });
    });
    this.form = this._formBuilder.group({
      year: [dayjs().year(), [Validators.required]],
      month: [dayjs().month() + 1, [Validators.required]],
      date_report: [dayjs().unix(), [Validators.required]],
      vessel_id: [this._profileState.profile().ids_vessel_id, [Validators.required]],
      remarks: [''],
      flag_copy: [false],
      report_type_id: [9]
    });
    this.disableFields();
  }

  disableFields() {
    if (isEmpty(this.previousReport())) {
      this.form.get('flag_copy').disable();
    }
  }

  ngOnDestroy() {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onSave() {
    this._action.actions.createNewReport({ body: this.form.getRawValue() });
    this._action.actions.createNewReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.getInventoriesReportWithCallback({}, () => this.save.emit());
      this.getVesselsReport({
        ...this.queryParams(),
        'filter[report_type_id]': 9
      });
    });
  }

  onDiscard() {
    this.close.emit();
  }

  private _subscribeToSource$<T>(source$: Observable<T>, callback: (value: T) => void): void {
    source$.pipe(takeUntil(this._unsubscribeAll)).subscribe((value: T) => {
      callback(value);

      this._changeDetectorRef.markForCheck();
    });
  }

  getInventoriesReportWithCallback(queryParams: Params, cb?: () => void): void {
    this._action.actions.getInventories({ queryParams });
    if (cb) {
      this._action.actions.getInventoriesSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        cb();
      });
    }
  }

  getVesselsReport(queryParams: Params): void {
    this._vesselReportsActions.actions.getVesselReports({ queryParams, vesselId: this._profileState.profile()?.id?.toString() });
  }
}
